import { useMemo } from "react";
import { useEvidenceSocketStore } from "./evidenceSocketStore";
import type { EvidenceItem } from "./evidenceSocketStore";

export const useFilteredEvidences = (): EvidenceItem[] => {
  const evidences = useEvidenceSocketStore((state) => state.evidences);
  const selectedAdapter = useEvidenceSocketStore(
    (state) => state.selectedAdapter
  );
  const selectedRetransmission = useEvidenceSocketStore(
    (state) => state.selectedRetransmission
  );
  const statusFilter = useEvidenceSocketStore((state) => state.statusFilter);
  const searchQuery = useEvidenceSocketStore(
    (state) => state.debouncedSearchQuery
  );

  return useMemo(() => {
    let result = evidences;

    if (selectedAdapter) {
      result = result.filter(
        (ev) => ev.integrator_adapter === selectedAdapter
      );
    }

    if (selectedRetransmission) {
      result = result.filter(
        (ev) => ev.retransmission_id === selectedRetransmission
      );
    }

    if (statusFilter === "ok") {
      result = result.filter((ev) => ev.integrator_ok);
    } else if (statusFilter === "error") {
      result = result.filter((ev) => !ev.integrator_ok);
    }

    if (searchQuery.trim()) {
      const normalizedQuery = searchQuery.toLowerCase().trim();
      const searchTerms = normalizedQuery.split(/\s+/);

      result = result.filter((ev) => {
        const searchableText = [
          ev.integrator_adapter,
          ev.ws_name,
          ev.retransmission_id,
          ev.device_id,
          ev.imei,
          ev.company_id,
          ev.job_id,
          ev.error,
          String(ev.http_status),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchTerms.every((term) => searchableText.includes(term));
      });
    }

    return result;
  }, [
    evidences,
    selectedAdapter,
    selectedRetransmission,
    statusFilter,
    searchQuery,
  ]);
};

export const useEvidenceAdapters = (): string[] => {
  const evidences = useEvidenceSocketStore((state) => state.evidences);

  return useMemo(() => {
    const adapters = new Set<string>();
    for (const ev of evidences) {
      if (ev.integrator_adapter) adapters.add(ev.integrator_adapter);
    }
    return Array.from(adapters).sort();
  }, [evidences]);
};

export const useEvidenceRetransmissions = (): {
  id: string;
  label: string;
}[] => {
  const evidences = useEvidenceSocketStore((state) => state.evidences);

  return useMemo(() => {
    const labels = new Map<string, string>();
    for (const ev of evidences) {
      if (!ev.retransmission_id) continue;
      if (!labels.has(ev.retransmission_id) || ev.ws_name) {
        labels.set(ev.retransmission_id, ev.ws_name || ev.retransmission_id);
      }
    }
    return Array.from(labels, ([id, label]) => ({ id, label })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [evidences]);
};

export const useEvidenceStats = () => {
  const evidences = useEvidenceSocketStore((state) => state.evidences);

  return useMemo(() => {
    let ok = 0;
    let error = 0;
    let latencySum = 0;
    for (const ev of evidences) {
      if (ev.integrator_ok) ok += 1;
      else error += 1;
      latencySum += ev.latency_ms || 0;
    }
    const total = evidences.length;
    return {
      total,
      ok,
      error,
      avgLatency: total > 0 ? Math.round(latencySum / total) : 0,
    };
  }, [evidences]);
};
